import React, { useState } from "react";
import { Trans } from "react-i18next";
import styled from "styled-components";
import { Button, Slide } from "@mui/material";
import Present from "icons/Present";
import DialogFullWindow from "components/other/DialogFullWindow";

const Dialog = styled(DialogFullWindow)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  width: 100%;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.duo.color.primary},
    ${({ theme }) => theme.duo.color.lightBlue}
  );
`;

const Content = styled.div`
  max-width: 360px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding-bottom: 32px;
`;

const Text = styled.div`
  font-family: "Noto Sans", "Segoe UI", sans-serif;
  text-align: center;
  color: #ffffff;
`;

const Title = styled(Text)`
  font-size: 28px;
  font-weight: 800;
  line-height: 1.3;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  margin: 24px 0;
`;

const Subtitle = styled(Text)`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 24px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const Middle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  justify-content: center;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  width: 100%;
  margin: 12px 0;
`;

const CardTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.duo.color.text};
  font-family: "Noto Sans", "Segoe UI", sans-serif;
  text-align: center;
`;

const Benefit = styled.div`
  display: flex;
  align-items: center;
  margin: 12px 0;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.duo.color.lightGrey};
  border-radius: 10px;
`;

const Icon = styled.div`
  width: 32px;
  height: 32px;
  margin-right: 12px;
  background-color: ${({ theme }) => theme.duo.color.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:after {
    content: "✓";
    color: white;
    font-weight: bold;
  }
`;

const BenefitText = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.duo.color.text};
  margin: 0;
  text-align: left;
`;

const ReminderText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.duo.color.text};
  margin: 16px 0 0;
  text-align: center;
  font-style: italic;
`;

const Highlight = styled.span`
  color: ${({ theme }) => theme.duo.color.primary};
  font-weight: 600;
`;

const Btn = styled(Button)`
  && {
    background: ${({ theme }) => theme.duo.color.white};
    color: ${({ theme }) => theme.duo.color.primary};
    text-transform: uppercase;
  }
`;

const BtnWrap = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 32px;
`;

const GiftBox = styled.div`
  background-color: #ffffff;
  border-radius: 16px;
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  margin: 20px 0;
`;

const GiftIcon = styled(Present)<{ $isVisible: boolean }>`
  width: 120px;
  height: 120px;
  opacity: ${(p) => (p.$isVisible ? 1 : 0)};
  transform: ${(p) => (p.$isVisible ? "scale(1)" : "scale(1.2) translateY(-40px)")};
  transition:
    opacity 0.7s ease-out,
    transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  filter: drop-shadow(0px 8px 15px rgba(0, 0, 0, 0.2));
  color: #ff3a5c;
`;

const Section = styled.div<{ $isVisible: boolean }>`
  opacity: ${(p) => (p.$isVisible ? 1 : 0)};
  transform: ${(p) => (p.$isVisible ? "translateY(0)" : "translateY(20px)")};
  transition:
    opacity 0.6s ease-in-out,
    transform 0.8s ease-in-out;
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  pointer-events: ${(p) => (p.$isVisible ? "all" : "none")};
  z-index: ${(p) => (p.$isVisible ? 2 : 1)};
`;

const TrialSubscriptionGift = ({ onSubmit }) => {
  const [showGift, setShowGift] = useState(true);
  const handleOpenGift = () => setShowGift(false);

  return (
    <Dialog open={true} TransitionComponent={Slide}>
      <Content>
        <Section $isVisible={showGift}>
          <Middle>
            <Title>
              <Trans>You are awesome, you just subscribed to our Premium plan!</Trans>
            </Title>
            <GiftBox>
              <GiftIcon $isVisible={showGift} onClick={handleOpenGift} />
            </GiftBox>
            <Subtitle>
              <Trans>We have a special gift for you</Trans>
            </Subtitle>
          </Middle>
          <BtnWrap>
            <Btn onClick={handleOpenGift} variant="contained" size="large">
              <Trans>Get Gift</Trans>
            </Btn>
          </BtnWrap>
        </Section>

        <Section $isVisible={!showGift}>
          <Middle>
            <Card>
              <CardTitle>
                <Trans>You’re getting the Family Plan, not just the regular one.</Trans>
              </CardTitle>

              <Benefit>
                <Icon />
                <BenefitText>
                  <Trans>
                    Invite up to <Highlight>5 members</Highlight> to share premium features
                  </Trans>
                </BenefitText>
              </Benefit>

              <Benefit>
                <Icon />
                <BenefitText>
                  <Trans>Learn together with friends and family</Trans>
                </BenefitText>
              </Benefit>

              <ReminderText>
                <Trans>Don't forget to add other members later in App.</Trans>
              </ReminderText>
            </Card>
          </Middle>
          <BtnWrap>
            <Btn onClick={onSubmit} variant="contained" size="large">
              <Trans>Ok, great</Trans>
            </Btn>
          </BtnWrap>
        </Section>
      </Content>
    </Dialog>
  );
};

export default TrialSubscriptionGift;
